import { FC, ReactElement } from 'react';

import { EvLink, EvTitle, HeadingLevelRange } from '@evinced-private/ui-common';

import { ExternalTicketFormatter } from 'src/common/components/external-ticket-formatter/ExternalTicketFormatter';
import {
	IElementInfo,
	IssueElementInfoFormatter
} from 'src/common/components/issue-element-info-formatter/IssueElementInfoFormatter';
import { MobileIssueComment } from 'src/common/components/mobile-issue-comment/MobileIssueComment';
import { formatSeverityText } from 'src/common/components/severity-cell-formatter/SeverityCellFormatterHelper';
import { TextBlockWithTitle } from 'src/common/components/text-block-with-title/TextBlockWithTitle';
import { ELEMENT_IDENTIFIER_TITLE } from 'src/common/helpers/MobileQueriesHelper';
import { MobileIssueDetails } from 'src/common/types/MobileSingleSessionTypes';

import './MobileIssueDetailsBlock.scss';

interface IMobileIssueDetailsBlockProps {
	issue: MobileIssueDetails;
	issueNumber: string;
	headingLevel?: HeadingLevelRange;
	actionButtons?: ReactElement | null;
}

const getElementInfoAriaLabel = (elementInfo: IElementInfo[], elementNumber: string): string => {
	if (!elementInfo.length) {
		return '';
	}

	const elementType =
		elementInfo.find(({ displayName }) => displayName === ELEMENT_IDENTIFIER_TITLE.ELEMENT_TYPE)
			?.value ?? '';

	return `Element ${elementNumber}${elementType ? `, Element type - ${elementType}` : ''}`;
};

export const MobileIssueDetailsBlock: FC<IMobileIssueDetailsBlockProps> = ({
	issue,
	issueNumber,
	headingLevel = 3,
	actionButtons
}) => {
	if (!issue) {
		return null;
	}

	const {
		type,
		severity,
		description,
		knowledgeBaseLink,
		expectations,
		elementInfo,
		a11yGuidelines,
		comment,
		taskManagerTickets
	} = issue;

	// todo: enable expectations when added to the schema
	const showExpectations = false;

	return (
		<div className="mobile-issue-details-block">
			<div className="issue-title-wrapper">
				<EvTitle
					headingLevel={headingLevel}
					className="mobile-issue mobile-issue-title issue-title"
					titleText={type}
				/>
				{actionButtons && <div className="issue-title-actions">{actionButtons}</div>}
			</div>
			<div className="issue-info">
				<TextBlockWithTitle
					name="Severity:"
					value={formatSeverityText(severity)}
					headingLevel={(headingLevel + 1) as HeadingLevelRange}
				/>
				{a11yGuidelines.length > 0 && (
					<div className="mobile-accessibility-guidelines">
						<TextBlockWithTitle
							name="Accessibility Guidelines:"
							value={
								<>
									{a11yGuidelines.map(({ title, link }) =>
										link.length > 0 ? (
											<EvLink
												className="success-criteria"
												key={`${title}-${link}`}
												url={link}
												openInNewTab={true}
												linkText={title}
											/>
										) : (
											<span key={title} className="success-criteria">
												{title}
											</span>
										)
									)}
								</>
							}
							headingLevel={(headingLevel + 1) as HeadingLevelRange}
						/>
					</div>
				)}
				{description && (
					<TextBlockWithTitle
						name="Description:"
						value={
							<>
								<p className="description-text">{description}</p>
								{knowledgeBaseLink.length > 0 && (
									<EvLink
										className="read-more"
										url={knowledgeBaseLink}
										openInNewTab={true}
										linkText="Read more in Knowledge Base"
									/>
								)}
							</>
						}
						headingLevel={(headingLevel + 1) as HeadingLevelRange}
					/>
				)}
			</div>
			{showExpectations && (
				<div className="expectations-section">
					<EvTitle
						headingLevel={(headingLevel + 1) as HeadingLevelRange}
						className="expectations-title"
						titleText="Expectations:"
					/>
					<ul className="expectations-list">
						{expectations.map((expectation) => (
							<li key={expectation} className="expectation-item">
								{expectation}
							</li>
						))}
					</ul>
				</div>
			)}
			{elementInfo.length > 0 && (
				<div
					className="element-info-section"
					aria-label={getElementInfoAriaLabel(elementInfo, issueNumber)}
				>
					<IssueElementInfoFormatter elementInfo={elementInfo} nodeNumber={issueNumber} />
					{comment && <MobileIssueComment comment={comment} />}
					{taskManagerTickets.length > 0 && (
						<div className="external-ticket">
							<span className="ticket-label">Added to tickets</span>
							<ExternalTicketFormatter tickets={taskManagerTickets} />
						</div>
					)}
				</div>
			)}
		</div>
	);
};
